import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  CheckCircle,
  RefreshCw,
  Link,
  Link2Off,
  Copy,
  Building2,
  Target,
  MapPin,
  User,
  ListTree,
  Archive,
  ArchiveRestore,
  Loader,
  Factory,
  MessageSquare,
  Users,
  X,
} from "lucide-react";
import { Permission, QuoteResponse, QuoteSettings } from "@forgepoint/shared";
import { GhostInput } from "./cells/GhostInput";
import { CompanySelector } from "@/components/CompanySelector";
import { ShippingAddressSelector } from "@/components/ShippingAddressSelector";
import { OpportunitySelector } from "@/components/OpportunitySelector";
import { UserSelector } from "@/components/UserSelector";
import { PeopleSelector } from "@/components/PeopleSelector";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { useHasAnyPermission } from "@/store/authStore";
import { PriceListSelector } from "@/components/PriceListSelector";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";
import { LocationSelector } from "@/components/LocationSelector";
import { QuotePrimaryThreadLink } from "./QuotePrimaryThreadLink";
import { QuoteMetaDateField } from "./QuoteMetaDateField";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
interface QuoteMetaBarProps {
  quote?: QuoteResponse | null;
  name: string;
  customerCompanyId: string | null;
  priceListId: string | null;
  shippingAddressId: string | null;
  locationId: string | null;
  opportunityId?: string | null;
  salesRepUserId: string | null;
  personIds: string[];
  expirationDate: string;
  quoteDate: string;
  readOnly: boolean;
  canEditOpportunity?: boolean;
  showLocationField: boolean;
  quoteSettings: QuoteSettings | null;
  onNameChange: (value: string) => void;
  onCustomerChange: (value: string | null) => void;
  onPriceListChange: (value: string | null) => void;
  onShippingAddressChange: (value: string | null) => void;
  onLocationChange: (value: string | null) => void;
  onOpportunityChange?: (value: string | null) => void;
  onSalesRepChange: (value: string | null) => void;
  onPeopleChange: (personIds: string[]) => void;
  onExpirationChange: (value: string) => void;
  onQuoteDateChange: (value: string) => void;
  onFinalize?: () => void;
  finalizeReviewThreadId?: string;
  onCreateRevision?: () => void;
  onCopyPublicLink?: () => void;
  onViewPublicQuote?: () => void;
  onRevokePublicAccess?: () => void;
  onRestorePublicAccess?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  isArchiving?: boolean;
}

/**
 * Quote header with the quote number, actions, and editable header fields, marking the fields the org requires.
 * @param props - The quote, header field values, required-field rules, and change handlers
 * @returns The quote header
 */
export function QuoteMetaBar({
  quote,
  name,
  customerCompanyId,
  priceListId,
  shippingAddressId,
  locationId,
  opportunityId,
  salesRepUserId,
  personIds,
  expirationDate,
  quoteDate,
  readOnly,
  canEditOpportunity = false,
  showLocationField,
  quoteSettings,
  onNameChange,
  onCustomerChange,
  onPriceListChange,
  onShippingAddressChange,
  onLocationChange,
  onOpportunityChange,
  onSalesRepChange,
  onPeopleChange,
  onExpirationChange,
  onQuoteDateChange,
  onFinalize,
  finalizeReviewThreadId,
  onCreateRevision,
  onCopyPublicLink,
  onViewPublicQuote,
  onRevokePublicAccess,
  onRestorePublicAccess,
  onArchive,
  onUnarchive,
  isArchiving,
}: QuoteMetaBarProps) {
  const navigate = useNavigate();
  const agentName = useStandardAgentDisplayName();
  const fetchPriceLists = usePriceListsStore((state) => state.fetchPriceLists);
  const [hasPriceLists, setHasPriceLists] = useState(false);
  const canViewPeople = useHasAnyPermission(
    Permission.PEOPLE_VIEW_ALL,
    Permission.PEOPLE_VIEW_OWN,
  );
  const upsertPeople = usePeopleStore((state) => state.upsertPeople);

  useEffect(() => {
    fetchPriceLists().then((ids) => setHasPriceLists(ids.length > 0));
  }, [fetchPriceLists]);

  useEffect(() => {
    upsertPeople(quote?.people ?? []);
  }, [quote?.people, upsertPeople]);

  const quoteNumber = quote
    ? `Quote #${quote.quoteDisplayNumber}${quote.revision > 1 ? `-R${quote.revision}` : ""}`
    : "New Quote";

  return (
    <div className="border-b bg-muted/30">
      {opportunityId && (
        <QuotePrimaryThreadLink opportunityId={opportunityId} />
      )}

      {/* Top Row: Quote Number, Name, Status, Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 pt-3 sm:pt-4 pb-2">
        <span className="text-sm font-medium text-muted-foreground">
          {quoteNumber}
        </span>
        {quote && !quote.isLatestRevision && (
          <Badge variant="outline" className="text-xs">
            Old Revision
          </Badge>
        )}
        {quote?.status === "FINALIZED" && (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Finalized
          </Badge>
        )}
        {quote?.publicAccessRevokedAt && (
          <Badge variant="destructive" className="gap-1">
            <Link2Off className="h-3 w-3" />
            Link Revoked
          </Badge>
        )}
        {quote?.archivedAt && (
          <Badge
            variant="outline"
            className="gap-1 border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400"
          >
            <Archive className="h-3 w-3" />
            Archived
          </Badge>
        )}
        <div className="flex-1" />
        {quote && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {quote.status !== "FINALIZED" &&
                (finalizeReviewThreadId || onFinalize) && (
                  <>
                    {finalizeReviewThreadId ? (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(
                            `/standard-agent?threadId=${finalizeReviewThreadId}`,
                          )
                        }
                      >
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Finalize this quote with {agentName}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={onFinalize}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Finalize Quote
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}
              {quote.status === "FINALIZED" && onCreateRevision && (
                <>
                  <DropdownMenuItem onClick={onCreateRevision}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Create Revision
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {onCopyPublicLink && (
                <DropdownMenuItem onClick={onCopyPublicLink}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Public URL
                </DropdownMenuItem>
              )}
              {onViewPublicQuote && (
                <DropdownMenuItem onClick={onViewPublicQuote}>
                  <Link className="mr-2 h-4 w-4" />
                  View Public Quote
                </DropdownMenuItem>
              )}
              {quote.status === "FINALIZED" &&
                !quote.publicAccessRevokedAt &&
                onRevokePublicAccess && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onRevokePublicAccess}>
                      <Link2Off className="mr-2 h-4 w-4" />
                      Revoke Public Access
                    </DropdownMenuItem>
                  </>
                )}
              {quote?.publicAccessRevokedAt && onRestorePublicAccess && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onRestorePublicAccess}>
                    <Link className="mr-2 h-4 w-4" />
                    Restore Public Access
                  </DropdownMenuItem>
                </>
              )}
              {quote?.archivedAt && onUnarchive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onUnarchive}
                    disabled={isArchiving}
                  >
                    {isArchiving ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArchiveRestore className="mr-2 h-4 w-4" />
                    )}
                    Unarchive Quote
                  </DropdownMenuItem>
                </>
              )}
              {!quote?.archivedAt && onArchive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onArchive} disabled={isArchiving}>
                    {isArchiving ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    Archive Quote
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Quote Name - Full Width */}
      <div className="px-3 sm:px-4 pb-3">
        <GhostInput
          value={name}
          onChange={onNameChange}
          placeholder="Quote name..."
          disabled={readOnly}
          className="text-lg sm:text-xl font-semibold h-10 w-full"
        />
      </div>

      {/* Bottom Rows: Customer, People, Opportunity, Sales Rep, Expiration Date - 2 per row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 px-3 sm:px-4 pb-4">
        {/* Customer */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            Customer
          </label>
          <CompanySelector
            currentlySelectedId={customerCompanyId}
            onSelectId={onCustomerChange}
            placeholder="Select customer"
            disabled={readOnly}
            className="w-full"
          />
        </div>

        {/* Ship To */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <span>
              Ship To
              {quoteSettings?.requireShipTo && (
                <span className="ml-0.5 text-destructive">*</span>
              )}
            </span>
          </label>
          <ShippingAddressSelector
            companyId={customerCompanyId}
            currentlySelectedId={shippingAddressId}
            onSelectId={onShippingAddressChange}
            disabled={readOnly || !customerCompanyId}
            size="sm"
            className="w-full"
          />
        </div>

        {canViewPeople && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              <span>
                People
                {quoteSettings?.requireContact && (
                  <span className="ml-0.5 text-destructive">*</span>
                )}
              </span>
            </label>
            <PeopleSelector
              mode="multi"
              selectedPersonIds={personIds}
              onPersonIdsChange={onPeopleChange}
              placeholder="Select people"
              disabled={readOnly}
              size="sm"
              className="w-full"
            />
          </div>
        )}

        {showLocationField && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Factory className="h-3 w-3" />
              <span>
                Location
                {quoteSettings?.requireLocation && (
                  <span className="ml-0.5 text-destructive">*</span>
                )}
              </span>
            </label>
            <LocationSelector
              currentlySelectedId={locationId}
              onSelectId={onLocationChange}
              placeholder="Select location"
              disabled={readOnly}
              className="w-full"
            />
          </div>
        )}

        {/* Opportunity */}
        {onOpportunityChange && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              Opportunity
            </label>
            <OpportunitySelector
              currentlySelectedId={opportunityId || null}
              onSelectId={onOpportunityChange}
              placeholder="Select opportunity"
              disabled={readOnly && !canEditOpportunity}
              variant="default"
              className="w-full"
            />
          </div>
        )}

        {(hasPriceLists || priceListId) && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <ListTree className="h-3 w-3" />
              Price List
            </label>
            <div className="flex items-center gap-1">
              <PriceListSelector
                currentlySelectedId={priceListId}
                onSelectId={onPriceListChange}
                placeholder="Select price list"
                disabled={readOnly}
                className="w-full"
              />
              {priceListId && !readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground"
                  onClick={() => onPriceListChange(null)}
                  aria-label="Clear price list"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Sales Rep */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <User className="h-3 w-3" />
            Sales Rep
          </label>
          <UserSelector
            currentlySelectedId={salesRepUserId}
            onSelectId={onSalesRepChange}
            placeholder="Select sales rep"
            disabled={readOnly}
            className="w-full"
          />
        </div>

        <QuoteMetaDateField
          label="Quote Date"
          value={quoteDate}
          readOnly={readOnly}
          onChange={onQuoteDateChange}
        />

        <QuoteMetaDateField
          label="Expiration Date"
          value={expirationDate}
          readOnly={readOnly}
          onChange={onExpirationChange}
        />
      </div>
    </div>
  );
}
